import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useBeforeUnload, useLocation } from "react-router-dom";
import { Row, Col, message, Card, Spin, Button, Space, Affix, BackTop, Tabs, Modal } from "antd";
import { ScheduleEditorProvider } from "./context/ScheduleEditorContext";
import ScheduleEditorHeader from "./components/ScheduleEditorHeader";
import { ScheduleBasicInfo } from "./components/ScheduleBasicInfo";
import FlightScheduleTable from "./components/FlightScheduleTable";
import FlightCompare from "./components/FlightCompare";
import enhancedApi from "../../../api/enhancedApi";
import { useAuth } from "../../../hooks/useAuth";

/**
 * 航班计划编辑器页面
 *
 * 注意: 该页面仅用于处理处于草稿(DRAFT)状态的计划
 * 非草稿状态的计划将无法编辑，会自动跳转到计划列表页面
 */
/**
 * 航班计划编辑器组件 (核心逻辑)
 * 可在页面路由中使用，也可在Modal中使用
 */
export const ScheduleEditor = ({ scheduleId, onClose, mode = "page" }) => {
    // 优先使用props传入的ID，如果是页面模式且没有props ID，则尝试从URL获取(虽然通常页面模式由Wrapper处理)
    const { id: paramId } = useParams();
    const id = scheduleId || (mode === "page" ? paramId : null);

    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const navigationAttemptRef = useRef(null);
    // 追踪是否进行过保存操作，以便在关闭时通知父组件刷新列表
    const hasSavedRef = useRef(false);
    // 使用React Router的useLocation获取state
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [initialSchedule, setInitialSchedule] = useState(null);
    const [error, setError] = useState(null);
    const [airportsMap, setAirportsMap] = useState({});
    const [loadingAirports, setLoadingAirports] = useState(false);
    // 添加tab激活状态
    const [activeTabKey, setActiveTabKey] = useState("flightSchedule");
    // 添加isDirty状态追踪
    const [isDirty, setIsDirty] = useState(false);

    // 处理保存成功的组件内回调
    const handleSaveSuccess = useCallback(() => {
        hasSavedRef.current = true;
    }, []);

    // 包装 onClose 回调，传递保存状态
    const handleCloseWrapper = useCallback(() => {
        if (onClose) {
            onClose(hasSavedRef.current);
        }
    }, [onClose]);

    // 监听Context中的isDirty变化
    const handleIsDirtyChange = useCallback((isDirty) => {
        setIsDirty(isDirty);
        // 同步到window对象，供AppSider组件使用
        window.scheduleDirtyState = isDirty;
    }, []);

    // 从location中获取state并设置初始activeTabKey
    useEffect(() => {
        // 检查是否有来自导航的state参数
        if (location.state && location.state.activeTab) {
            setActiveTabKey(location.state.activeTab);

            // 清除state防止刷新页面后再次应用
            const newUrl = `${location.pathname}${location.search}`;
            window.history.replaceState({}, document.title, newUrl);
        }
    }, [location]);

    // 使用useBeforeUnload钩子实现页面关闭提醒
    useBeforeUnload(
        useCallback(
            (event) => {
                if (isDirty) {
                    // 标准的beforeunload事件处理
                    event.preventDefault();
                    // Chrome需要设置returnValue
                    event.returnValue = "您有未保存的更改，确定要离开吗？";
                    return "您有未保存的更改，确定要离开吗？";
                }
            },
            [isDirty],
        ),
    );

    // 自定义导航函数，用于替代useBlocker
    const customNavigate = useCallback(
        (to, options) => {
            if (isDirty) {
                // 存储导航尝试
                navigationAttemptRef.current = { to, options };

                // 显示确认对话框
                Modal.confirm({
                    title: "未保存提醒",
                    content: "您有未保存的更改，确定要离开吗？",
                    okText: "确认离开",
                    cancelText: "取消",
                    onOk: () => {
                        // 确认离开，继续导航
                        const { to, options } = navigationAttemptRef.current;
                        navigate(to, options);
                        navigationAttemptRef.current = null;
                    },
                    onCancel: () => {
                        // 取消导航
                        navigationAttemptRef.current = null;
                    },
                });
            } else {
                // 没有未保存的更改，直接导航
                navigate(to, options);
            }
        },
        [isDirty, navigate],
    );

    // 监听导航事件
    useEffect(() => {
        // 在组件挂载时设置事件监听器，拦截所有A链接点击
        const handleLinkClick = (e) => {
            // 如果点击事件是在可编辑元素上触发的，不做处理
            if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable || e.target.closest(".ant-input") || e.target.closest(".ant-select") || e.target.closest(".ant-picker")) {
                return;
            }

            // 检查是否是A标签点击
            let element = e.target;
            while (element && element.tagName !== "A") {
                element = element.parentElement;
            }

            if (element && element.tagName === "A" && element.getAttribute("href") && element.getAttribute("href").startsWith("/") && !element.getAttribute("target")) {
                // 是内部导航链接
                if (isDirty) {
                    e.preventDefault();
                    const href = element.getAttribute("href");
                    customNavigate(href);
                }
                // 如果不是dirty状态，让默认导航行为发生
            }
        };

        // 添加事件监听器 - 使用捕获阶段处理
        document.addEventListener("click", handleLinkClick, { capture: true });

        // 清理事件监听器
        return () => {
            document.removeEventListener("click", handleLinkClick, { capture: true });
        };
    }, [isDirty, customNavigate]);

    // 在ScheduleEditor组件组件挂载时向window添加currentScheduleEditor引用，方便其他组件使用
    useEffect(() => {
        // 添加当前实例引用
        window.currentScheduleEditor = {
            customNavigate,
            isDirty,
            hasSaved: hasSavedRef.current,
        };

        // 重写window对象上的导航方法，供其他组件使用
        window.scheduleEditorNavigate = customNavigate;

        return () => {
            // 清理引用
            delete window.currentScheduleEditor;
            delete window.scheduleEditorNavigate;
        };
    }, [customNavigate, isDirty]);

    // 检查权限
    useEffect(() => {
        if (!hasPermission("schedule:update")) {
            message.error("您没有权限访问此页面");
            if (mode === "modal" && onClose) {
                onClose(false);
            } else {
                navigate("/", { replace: true });
            }
        }
    }, [hasPermission, navigate, onClose, mode]);

    // 获取机场数据并缓存
    const fetchAirportsData = useCallback(async () => {
        try {
            // 如果已经加载过，则直接返回
            if (Object.keys(airportsMap).length > 0) return;

            setLoadingAirports(true);
            // 检查全局缓存中是否已有机场数据
            if (window.cachedAirports && Array.isArray(window.cachedAirports) && window.cachedAirports.length > 0) {
                // 转换为映射对象，方便查找
                const airportsObj = {};
                window.cachedAirports.forEach((airport) => {
                    airportsObj[airport.iata_code] = airport;
                });
                setAirportsMap(airportsObj);
                return;
            }

            const response = await enhancedApi.masterData.airport.getAirportList({ page_size: 500 }); // 添加 page_size
            // 从 response.data.items 获取列表数据，并进行空值检查
            const airportList = response?.data?.items || [];

            if (airportList.length > 0) {
                // 缓存到全局
                window.cachedAirports = airportList;

                // 转换为映射对象，方便查找
                const airportsObj = {};
                airportList.forEach((airport) => {
                    airportsObj[airport.iata_code] = airport;
                });
                setAirportsMap(airportsObj);
            }
        } catch (error) {
            console.error("获取机场数据失败:", error);
            message.error("获取机场数据失败");
        } finally {
            setLoadingAirports(false);
        }
    }, [airportsMap]);

    // 页面加载时获取机场数据
    useEffect(() => {
        fetchAirportsData();
    }, [fetchAirportsData]);

    // 使用增强API服务加载计划详情
    const fetchSchedule = async (scheduleId) => {
        try {
            setLoading(true);

            // 使用增强API服务，日期时间字段会自动处理
            const response = await enhancedApi.schedule.getScheduleDetail(scheduleId, { limit: 300 }); // 确保有足够的航班数据
            let data = response?.data;

            if (data) {
                // 检查参数是否嵌套在 data.parameter.parameter_json
                if (data.parameter && data.parameter.parameter_json) {
                    // 如果顶层 parameters 不存在，则创建
                    if (!data.parameters) {
                        data.parameters = {};
                    }
                    // 将 parameter_json 的内容合并到顶层 parameters
                    // 确保合并时不会丢失 context reducer 中特意保留的字段 (如 flight_compare_result)
                    // 所以我们先复制现有的 data.parameters (如果存在)，再合并 parameter_json
                    data.parameters = {
                        ...data.parameters, // 保留可能已存在的 context 内部字段
                        ...data.parameter.parameter_json, // 合并后端返回的参数
                    };
                } else if (!data.parameters) {
                    // 如果参数既不嵌套，顶层也不存在，则初始化
                    console.warn("计划详情响应中未找到预期的参数结构 (data.parameter.parameter_json)，正在初始化参数。");
                    data.parameters = {
                        transportation_demands: [],
                        available_segments: [],
                        min_ground_time: 60,
                        flight_number_prefix: "",
                        flight_compare_result: null, // 确保 context reducer 需要的字段存在
                        curfew_constraints: [],
                        maintenance_requirements: {},
                    };
                }

                // --- 确保关键参数字段有默认值 ---
                if (!data.parameters.transportation_demands) {
                    console.warn("计划参数中缺少 transportation_demands，初始化为空数组。");
                    data.parameters.transportation_demands = [];
                }
                if (!data.parameters.available_segments) data.parameters.available_segments = [];
                if (data.parameters.min_ground_time === undefined || data.parameters.min_ground_time === null) data.parameters.min_ground_time = 60;
                if (data.parameters.flight_number_prefix === undefined || data.parameters.flight_number_prefix === null) data.parameters.flight_number_prefix = "";
                if (data.parameters.curfew_constraints === undefined) data.parameters.curfew_constraints = [];
                if (data.parameters.maintenance_requirements === undefined) data.parameters.maintenance_requirements = {};
                if (data.parameters.flight_compare_result === undefined) data.parameters.flight_compare_result = null;

                // 检查计划状态，只允许编辑草稿状态的计划
                if (data.status !== "DRAFT") {
                    console.error("只能编辑草稿状态的计划");
                    setError("只能编辑草稿状态的计划");
                    message.error("只能编辑草稿状态的计划");
                    if (mode === "modal" && onClose) {
                        onClose(false);
                    } else {
                        navigate("/flightSchedule/drafts", { replace: true });
                    }
                    return;
                }

                // 确保航班按照display_order排序
                if (data.flights && data.flights.length > 0) {
                    // 按照display_order, flight_date, flight_number排序
                    data.flights.sort((a, b) => {
                        // 优先按display_order排序
                        if (a.display_order !== undefined && b.display_order !== undefined) {
                            return a.display_order - b.display_order;
                        }
                        // 如果没有display_order, 则按照flight_date排序
                        if (a.flight_date !== b.flight_date) {
                            return new Date(a.flight_date) - new Date(b.flight_date);
                        }
                        // 最后按照flight_number排序
                        return a.flight_number.localeCompare(b.flight_number);
                    });
                }

                setInitialSchedule(data);
            }
        } catch (error) {
            console.error("获取航班计划详情出错:", error);
            setError(`获取计划详情失败: ${error.message || "未知错误"}`);
            message.error(`获取计划详情失败: ${error.message || "未知错误"}`);
        } finally {
            setLoading(false);
        }
    };

    // 组件挂载时加载数据
    useEffect(() => {
        if (id && id !== "new") {
            fetchSchedule(id);
        } else {
            // 新建计划时的默认值
            setLoading(false);
            // 为新计划设置默认的运输需求
            const defaultTransportationDemands = [
                {
                    id: "default-hkg-lgg-" + Date.now(),
                    origin: "HKG",
                    destination: "LGG",
                    frequency: 3,
                    preferred_time: "08:00",
                },
                {
                    id: "default-hgh-bud-" + (Date.now() + 1),
                    origin: "HGH",
                    destination: "BUD",
                    frequency: 1,
                    preferred_time: "08:00",
                },
                {
                    id: "default-cgo-bud-" + (Date.now() + 1),
                    origin: "CGO",
                    destination: "BUD",
                    frequency: 1,
                },
            ];

            // 确保新计划的状态设置为DRAFT
            setInitialSchedule({
                name: "新建航班计划",
                start_date: null,
                end_date: null,
                status: "DRAFT", // 明确设置为草稿状态
                tags: [],
                flights: [],
                parameters: {
                    flight_number_prefix: "",
                    min_ground_time: 60,
                    transportation_demands: defaultTransportationDemands,
                    available_segments: [],
                },
            });
        }
    }, [id]);

    // 组件卸载时清理全局状态
    useEffect(() => {
        return () => {
            // 清理window全局对象上的状态
            delete window.scheduleDirtyState;
        };
    }, []);

    // 错误状态显示
    if (error && !loading) {
        return (
            <Card title="加载失败" style={{ margin: 20 }}>
                <p>{error}</p>
                <p>
                    <a
                        onClick={() => {
                            if (mode === "modal" && onClose) {
                                onClose(false);
                            } else {
                                navigate("/schedules");
                            }
                        }}
                    >
                        返回列表
                    </a>
                </p>
            </Card>
        );
    }

    // 加载状态显示
    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
                <Spin size="large" tip="加载航班计划中..." />
            </div>
        );
    }

    // 处理Tab切换
    const handleTabChange = (key) => {
        setActiveTabKey(key);
    };

    // 定义Tab项
    const tabItems = [
        {
            key: "flightSchedule",
            label: "航班编排",
            children: <FlightScheduleTable airportsMap={airportsMap} />,
        },
        {
            key: "flightCompare",
            label: "航班对比",
            children: <FlightCompare airportsMap={airportsMap} />,
        },
    ];

    return (
        <ScheduleEditorProvider initialSchedule={initialSchedule} onIsDirtyChange={handleIsDirtyChange}>
            <div style={{ padding: mode === "modal" ? "0" : "0 20px 50px 20px" }} className="schedule-editor-container">
                <ScheduleEditorHeader isNew={id === "new" || !id} mode={mode} onClose={handleCloseWrapper} onSaveSuccess={handleSaveSuccess} />
                <Row gutter={[0, 16]} style={{ marginTop: 16 }}>
                    <Col span={24} id="section-basic-info">
                        <ScheduleBasicInfo />
                    </Col>
                    <Col span={24} id="section-tabs">
                        <Tabs activeKey={activeTabKey} onChange={handleTabChange} items={tabItems} size="large" animated={{ tabPane: true }} style={{ marginBottom: 16 }} />
                    </Col>
                </Row>

                <BackTop />

                <style>{`
                    .schedule-editor-container {
                        scroll-behavior: smooth;
                        /* 在Modal模式下，限制高度并允许滚动 */
                        ${mode === "modal" ? "height: calc(100vh - 60px); overflow-y: auto; overflow-x: hidden;" : ""}
                    }
                    .ant-card {
                        margin-bottom: 16px;
                    }
                    .ant-tabs-content {
                        min-height: 400px;
                    }

                `}</style>
            </div>
        </ScheduleEditorProvider>
    );
};

/**
 * 航班计划编辑器页面入口 (路由组件)
 */
const ScheduleEditorPage = () => {
    const { id } = useParams();
    return <ScheduleEditor scheduleId={id} mode="page" />;
};

export default ScheduleEditorPage;
